<template>
  <div class="container">
    <div class="calculatorContainer">
      <div class="columns">
        <div class="calculator">
          <h1>Staking ROI Calculator</h1>
          <div class="calc-section">
            <label class="calc-subheader">Tokens Staked</label>
            <div>
              <input
                v-model="stakingInput"
                type="text"
                class="stakingInput"
                @focus="$event.target.select()"
              />
              SCRT
            </div>
          </div>
          <div class="calc-section">
            <label class="calc-subheader"> Validator</label>
            <div class="dropdownList">
              <select
                v-model="selectedValidator"
                name="validatorOptions"
                class="validatorDropdown"
              >
                <option :value="secureSecrets">{{ secureSecrets.name }}</option>
                <option
                  v-for="(item, index) in validatorList"
                  :key="index"
                  :value="item"
                >
                  {{ item.name }}
                </option>
              </select>
            </div>
            <div>
              {{ selectedValidator.rate | convertToPercent }}
              %
              <label>Commission Fee</label>
            </div>
          </div>
          <div class="calc-section">
            <label class="calc-subheader">Compound Frequency</label>
            <div class="dropdownList">
              <select v-model="compoundingInput" name="compoundingOptions">
                <option
                  v-for="item in compoundingOptions"
                  :key="item.value"
                  :value="item.value"
                >
                  {{ item.text }}
                </option>
              </select>
            </div>
          </div>
          <div class="calculatorButtonDiv">
            <button @click="calculateReturns" class="button StakeButton">
              Calculate
            </button>
          </div>
          <div class="calc-section">
            <div
              id="growthrate"
              :style="{ visibility: showOutput ? 'visible' : 'hidden' }"
            >
              <label class="calc-subheader">Growth Rate</label>
              <div>
                <output
                  v-if="!calculationStarted"
                  type="text"
                  style="visibility: visible"
                  >{{ outputMessage }}</output
                >
                <output v-else type="text" id="calculationOutput">{{
                  growthRateOutput | convertToPercent
                }}</output>
                <label>&nbsp;%</label>
              </div>
            </div>
          </div>
          <div class="calc-section">
            <div
              id="monthlyearnings"
              :style="{ visibility: showOutput ? 'visible' : 'hidden' }"
            >
              <label class="calc-subheader">{{ intervalReturnTitle }}</label>
              <div>
                <output type="text">{{
                  intervalReturnSCRTOutput | roundCalcTenth
                }}</output>
                <label> &nbsp;SCRT</label>
              </div>
              <div>
                <output type="text">{{
                  intervalReturnUSDOutput | roundCalcTenth
                }}</output>
                <label> &nbsp;USD*</label>
              </div>
            </div>
          </div>
          <div class="calc-section">
            <div
              id="yearlyearnings"
              :style="{ visibility: showOutput ? 'visible' : 'hidden' }"
            >
              <label class="calc-subheader">Yearly Returns</label>
              <div>
                <output type="text">{{
                  yearlyReturnSCRTOutput | roundCalcTenth
                }}</output>
                <label> &nbsp;SCRT</label>
              </div>
              <div>
                <output type="text">{{
                  yearlyReturnUSDOutput | roundCalcTenth
                }}</output>
                <label> &nbsp;USD*</label>
              </div>
            </div>
          </div>
          <div class="USDdisclaimer">
            *USD returns are based on current SCRT price which is subject to
            change.
          </div>
        </div>
      </div>
    </div>
    <h1>How does this work?</h1>
    <figure class="equation">
      <img src="@/assets/StakingEquation1.png" height="65px" />
    </figure>
    <ol class="equationVariables">
      <li>I = Inflation Rate</li>
      <li>F = Foundation Tax</li>
      <li>C = Validator Commission Rate</li>
      <li>T = Community Tax</li>
      <li>R = Staking Ratio</li>
    </ol>
    <figure class="equation">
      <img src="@/assets/StakingEquation2.png" height="65px" />
    </figure>
    <br />
    <h2>Calculator Parameters</h2>
    <div class="parametersSection">
      <div class="parameterLabelColumn">
        <label>Inflation Rate</label>
        <label>Foundation Tax</label>
        <label>Community Tax</label>
        <label>Total Staked SCRT</label>
        <label>Total SCRT Supply</label>
        <label>SCRT Price (USD)</label>
      </div>
      <div class="parameterOutputColumn">
        <output type="text"> {{ inflationRate | roundCalcHundredth }} </output>
        <output type="text">{{ foundationTax | roundCalcHundredth }}</output>
        <output type="text">{{ communityTax | roundCalcHundredth }}</output>
        <output type="text">{{ totalSCRTStaked | addCommas }}</output>
        <output type="text">{{ totalSCRTSupply | addCommas }}</output>
        <output type="text">{{ priceSCRT }}</output>
      </div>
    </div>
  </div>
</template>
<script>
var images = require.context("../assets/", false, /\.png$|\.jpg$/);

// Global Variables

var mainUrl = "https://api.scrt.network/";
var scrtPriceURL =
  "https://api.coingecko.com/api/v3/simple/price?ids=Secret&vs_currencies=USD";
var secureSecretsAddress =
  "secretvaloper1rfnmcuwzf3zn7r025j9zr3ncc7mt9ge56l7se7";

var validatorCalculatorLoaded = false;
var validatorListArchive;
var secureSecretsArchive;
var calculatorParametersArchive = {};

export default {
  metaInfo: {
    title: "Staking Calculator",
    meta: [
      {
        name: "description",
        content:
          "View your return on investment (ROI) for staking your SCRT tokens with Secure Secrets or other validators on the Secret Network.",
      },
    ],
  },

  data: () => ({
    stakingInput: 0,
    stakingInputErrorShow: false,
    showOutput: false,
    validatorInput: "",
    calculationStarted: false,
    outputMessage: "Calculation Output...",
    compoundingOptions: [
      { text: "None", value: 0 },
      { text: "Quarterly", value: 4 },
      { text: "Monthly", value: 12 },
      { text: "Biweekly", value: 26 },
      { text: "Weekly", value: 52 },
      { text: "Daily", value: 365 },
    ],
    validatorList: [],
    secureSecrets: { name: "hi", rate: 0, address: "TBD" },
    selectedValidator: { rate: 0 },
    compoundingInput: 0,
    growthRateOutput: "",
    intervalReturnTitle: "Monthly Return",
    intervalReturnSCRTOutput: 0,
    intervalReturnUSDOutput: 0,
    yearlyReturnSCRTOutput: 0,
    yearlyReturnUSDOutput: 0,
    inflationRate: 0,
    totalSCRTSupply: 0,
    totalSCRTStaked: 0,
    foundationTax: 0,
    communityTax: 0,
    priceSCRT: 0,
  }),
  created() {
    if (!validatorCalculatorLoaded) {
      this.populateValidatorList();
      this.getCalculatorParameters();
      validatorCalculatorLoaded = true;
    } else {
      this.secureSecrets = secureSecretsArchive;
      this.selectedValidator = secureSecretsArchive;
      this.validatorList = validatorListArchive;
      this.inflationRate = calculatorParametersArchive.inflationRate;
      this.totalSCRTSupply = calculatorParametersArchive.totalSCRTSupply;
      this.totalSCRTStaked = calculatorParametersArchive.totalSCRTStaked;
      this.foundationTax = calculatorParametersArchive.foundationTax;
      this.communityTax = calculatorParametersArchive.communityTax;
      this.priceSCRT = calculatorParametersArchive.priceSCRT;
    }
  },

  filters: {
    addCommas: function (input) {
      return Math.round(Number(input))
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    roundCalcHundredth: function (input) {
      return Math.round(Number(input) * 100) / 100;
    },
    roundCalcTenth: function (input) {
      return Math.round(Number(input) * 10) / 10;
    },
    convertToPercent: function (input) {
      return Math.round(input * 10000) / 100;
    },
  },

  methods: {
    calculateReturns() {
      if (this.validateStakedTokenInput(this.stakingInput) === false) {
        this.outputMessage = "Error! please enter a valid SCRT Input";
        this.calculationStarted = false;
        this.showOutput = false;
      } else {
        this.stakingInputErrorShow = false;
        this.calculationStarted = true;

        this.growthRateOutput = this.calcROI(
          this.stakingInput,
          this.selectedValidator.rate
        );
        if (this.compoundingInput === 0) {
          this.intervalReturnTitle = "Monthly Returns";
          this.intervalReturnSCRTOutput =
            (this.growthRateOutput * this.stakingInput) / 12;
          this.intervalReturnUSDOutput =
            this.intervalReturnSCRTOutput * this.priceSCRT;
          this.yearlyReturnSCRTOutput =
            this.growthRateOutput * this.stakingInput;
          this.yearlyReturnUSDOutput =
            this.growthRateOutput * this.stakingInput * this.priceSCRT;
        } else {
          let compoundingLabel;
          for (let i = 0; i < this.compoundingOptions.length; i++) {
            if (this.compoundingOptions[i].value === this.compoundingInput) {
              compoundingLabel = this.compoundingOptions[i].text;
            }
          }

          this.intervalReturnTitle = "First " + compoundingLabel + " Returns";
          this.intervalReturnSCRTOutput =
            (this.stakingInput * this.growthRateOutput) / this.compoundingInput;
          this.intervalReturnUSDOutput =
            this.intervalReturnSCRTOutput * this.priceSCRT;
          this.yearlyReturnSCRTOutput =
            this.stakingInput *
              Math.pow(
                1 + this.growthRateOutput / this.compoundingInput,
                this.compoundingInput
              ) -
            this.stakingInput;
          this.yearlyReturnUSDOutput =
            this.yearlyReturnSCRTOutput * this.priceSCRT;
        }
        this.showOutput = true;
      }
    },

    validateStakedTokenInput: function (input) {
      if (isNaN(input) === false && input >= 0) {
        return true;
      } else {
        return false;
      }
    },

    // TODO fix function
    /*
       onlyLettersAndNumbers(str) {

        },
*/
    sortOn: function (property) {
      return function (a, b) {
        // TODO sort regardless of upper or lowercase
        /* console.log(a[property].match("^[A-Za-z0-9]+$"))
                if (this.onlyLettersAndNumbers(a[property])){
                    a[property] = a[property].toLowerCase()
                }
                if (this.onlyLettersAndNumbers(b[property])){
                    b[property] = b[property].toLowerCase()
                } */
        if (a[property] < b[property]) {
          return -1;
        } else if (a[property] > b[property]) {
          return 1;
        } else {
          return 0;
        }
      };
    },
    imgUrl: function (path) {
      return images("./" + path);
    },

    // Calculations
    calcROI(newStakedTokens, commissionRate) {
      let totalStaked = this.totalSCRTStaked + Number(newStakedTokens);
      let ROI =
        (this.inflationRate / (totalStaked / this.totalSCRTSupply)) *
        (1 - this.foundationTax - this.communityTax) *
        (1 - commissionRate);
      return ROI;
    },

    async populateValidatorList() {
      let response = await fetch(mainUrl + "staking/validators");
      let responseJson = await response.json();

      let tempArray = [];
      for (let i = 0; i < responseJson.result.length; i++) {
        let element = {};
        const moniker = responseJson.result[i].description.moniker;
        const rate = responseJson.result[i].commission.commission_rates.rate;
        const address = responseJson.result[i].operator_address;

        element.name = moniker;
        element.rate = rate;
        element.address = address;
        tempArray.push(element);
        if (element.address === secureSecretsAddress) {
          this.secureSecrets = element;
          secureSecretsArchive = element;
          this.selectedValidator = element;
        }
      }
      tempArray.sort(this.sortOn("name"));
      for (let i = 0; i < tempArray.length; i++) {
        this.validatorList.push(tempArray[i]);
      }
      validatorListArchive = this.validatorList;
    },

    // API Queries
    async getInflation() {
      let response = await fetch(mainUrl + "minting" + "/" + "inflation");
      let responseJson = await response.json();
      return responseJson.result;
    },

    async getTotalSupply() {
      let response = await fetch(mainUrl + "cosmos/bank/v1beta1/supply");
      let responseJson = await response.json();
      var scrtSupplyIndex = 0;
      for (let i = 0; i < responseJson.supply.length; i++) {
        if (responseJson.supply[i].denom === "uscrt") {
          scrtSupplyIndex = i;
        }
      }
      return responseJson.supply[scrtSupplyIndex].amount / 1000000;
    },

    async getTotalStaked() {
      let response = await fetch(mainUrl + "staking/pool");
      let responseJson = await response.json();
      return responseJson.result.bonded_tokens / 1000000;
    },

    async getTaxes() {
      let response = await fetch(mainUrl + "distribution/parameters");
      let responseJson = await response.json();
      return {
        foundationTax: responseJson.result.secret_foundation_tax,
        communityTax: responseJson.result.community_tax,
      };
    },

    async getSCRT_Price() {
      let response = await fetch(scrtPriceURL);
      let responseJson = await response.json();
      return responseJson.secret.usd;
    },

    async getCalculatorParameters() {
      this.inflationRate = await this.getInflation();
      this.totalSCRTSupply = await this.getTotalSupply();
      this.totalSCRTStaked = await this.getTotalStaked();

      let taxes = await this.getTaxes();
      this.foundationTax = taxes.foundationTax;
      this.communityTax = taxes.communityTax;

      this.priceSCRT = await this.getSCRT_Price();

      // archive parameters for after view reload
      calculatorParametersArchive.inflationRate = this.inflationRate;
      calculatorParametersArchive.totalSCRTSupply = this.totalSCRTSupply;
      calculatorParametersArchive.totalSCRTStaked = this.totalSCRTStaked;
      calculatorParametersArchive.foundationTax = this.foundationTax;
      calculatorParametersArchive.communityTax = this.communityTax;
      calculatorParametersArchive.priceSCRT = this.priceSCRT;
    },
  },
};
</script>
<style scoped>
* {
  margin: 0;
  padding: 0;
  list-style: none;
  text-decoration: none;
  box-sizing: border-box;
  font-family: "Roboto", sans-serif;
}

*,
*:before,
*:after {
  box-sizing: inherit;
}

.container {
  max-width: 1500px;
  margin: 0 auto;
  padding-bottom: 60px;
  min-height: calc(100vh - 300px);
}

.columns {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 95%;
  margin: 2rem auto;
}

.calculator {
  margin: 0 auto;
  color: white;
  width: 700px;
  background-color: #252b40;
  padding-bottom: 10px;
  box-shadow: 0px 0px 17px -4px #000000;
}

.calc-section {
  display: flex;
  flex-direction: column;
  border-style: solid;
  border-color: #303b59;
  text-align: center;
  margin: 10px 90px;
  padding: 5px 0px;
  line-height: 30px;
}

.validatorcalculator .calc-section {
  border-color: #455283;
}

.calc-subheader {
  font-size: 20px;
  font-weight: 600;
}

input {
  text-align: right;
}

.stakingInput {
  width: 100px;
}

.dropdownList {
  margin: 0 auto;
  text-align: center;
}

.calculatorButtonDiv {
  text-align: center;
}

.calculator .button {
  padding: 5px 32px;
}

.StakeButton {
  background-color: rgb(20, 26, 60);
  border-radius: 5px;
  color: white;
  font-size: 20px;
}

.calculator .StakeButton {
  background-color: #627ae3;
}

.StakeButton span {
  cursor: pointer;
  display: inline-block;
  position: relative;
  transition: 0.5s;
}

.StakeButton span:after {
  content: "\00bb";
  position: relative;
  opacity: 0;
  top: 0;
  right: 0px;
  transition: 0.5s;
}

.StakeButton:hover span {
  padding-right: 15px;
}

.StakeButton:hover span:after {
  opacity: 1;
  right: 1;
}

.button:hover {
  background-color: #555;
  background-color: #323d55;
  cursor: pointer;
}

.calculator h1 {
  padding: 20px 10px;
  font-size: 40px;
  color: white;
}

select,
option {
  text-align: center;
}

.USDdisclaimer {
  text-align: center;
}

.equationVariables {
  margin: 20px auto;
  width: 275px;
  font-size: larger;
  text-align: left;
}

.parameterLabelColumn {
  display: flex;
  flex-direction: column;
  width: 250px;
}

.parameterOutputColumn {
  display: flex;
  flex-direction: column;
}

.parametersSection {
  display: flex;
  justify-content: center;
  font-size: larger;
  margin: 0px 50px 20px 20px;
  padding: 10px;
  text-align: left;
}

.calculatorContainer {
  padding-bottom: 0;
}

.validatorDropdown {
  max-width: 200px;
}

@media (max-width: 600px) {
  .equation img {
    height: 50px;
  }
}

@media (max-width: 420px) {
  .equation img {
    height: 45px;
  }
}

@media (max-width: 750px) {
  .calculator {
    width: 100%;
  }
  .calc-section {
    margin: 10px 10px;
  }
}

@media (max-width: 420px) {
  #equationLong {
    font-size: 9.5px;
  }
  .equationVariables {
    font-size: 15px;
  }
  .parametersSection {
    font-size: 15px;
  }
}
</style>
